import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './css/RentRoomList.module.css';

const RentRoomList = ({ rooms, onSelectRoom, isVerifyingSession, selectedRoomId }) => {
    return (
        <div className={styles.roomList}>
            {rooms.length > 0 ? (
                rooms.map((room) => (
                    <RoomCard 
                        key={room._id} 
                        room={room} 
                        onSelectRoom={onSelectRoom}
                        isVerifying={isVerifyingSession && selectedRoomId === room._id}
                    />
                ))
            ) : (
                <div className={styles.noRooms}>
                    <div className={styles.noRoomsIcon}>🏡</div>
                    <h3>No apartments available</h3>
                    <p>Please check back later for availability</p>
                </div>
            )}
        </div>
    );
};

const RoomCard = ({ room, onSelectRoom, isVerifying }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isImageFullscreen, setIsImageFullscreen] = useState(false);

    const nextImage = (e) => {
        e.stopPropagation();
        if (!room.files?.length) return;
        setCurrentImageIndex((prevIndex) =>
            prevIndex === room.files.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = (e) => {
        e.stopPropagation();
        if (!room.files?.length) return;
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? room.files.length - 1 : prevIndex - 1
        );
    };

    const openFullscreen = (e) => {
        e.stopPropagation();
        if (room.files?.length > 0) {
            setIsImageFullscreen(true);
        }
    };

    const closeFullscreen = () => {
        setIsImageFullscreen(false);
    };

    const handleCardClick = (e) => {
        if (isVerifying) return;
        // Don't open booking form if click was on image slider or fullscreen button
        if (e.target.closest(`.${styles.roomImageSlider}`) || 
            e.target.closest(`.${styles.fullscreenButton}`) ||
            e.target.closest(`.${styles.sliderArrow}`) ||
            e.target.closest(`.${styles.indicatorDot}`)) {
            return;
        }
        onSelectRoom(room);
    };

    return (
        <>
            <div 
                className={styles.roomCard}
                onClick={handleCardClick}
            >
                <div className={styles.roomImageSlider}>
                    {room.files?.length > 0 ? (
                        <>
                            <img 
                                src={`${room.files[currentImageIndex]?.url}`} 
                                alt={`${room.roomType} - Image ${currentImageIndex + 1}`}
                                onError={(e) => {
                                    e.target.src = '/placeholder-property.jpg';
                                }}
                                className={styles.roomImage}
                                onClick={openFullscreen}
                                style={{ cursor: 'pointer' }}
                            />

                            <button 
                                className={styles.fullscreenButton}
                                onClick={openFullscreen}
                                aria-label="View full screen"
                                disabled={!room.files?.length}
                            >
                                🔍
                            </button>

                            {room.files.length > 1 && (
                                <>
                                    <button 
                                        className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}
                                        onClick={prevImage}
                                        aria-label="Previous image"
                                    >
                                        ‹
                                    </button>
                                    <button 
                                        className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}
                                        onClick={nextImage}
                                        aria-label="Next image"
                                    >
                                        ›
                                    </button>
                                </>
                            )}

                            {room.files.length > 1 && (
                                <div className={styles.imageIndicators}>
                                    {room.files.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`${styles.indicatorDot} ${index === currentImageIndex ? styles.active : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentImageIndex(index);
                                            }}
                                            aria-label={`Go to image ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <img 
                            src="/placeholder-property.jpg" 
                            alt="Default apartment" 
                            className={styles.roomImage}
                            onClick={openFullscreen}
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                </div>

                <div className={styles.roomDetails}>
                    <div className={styles.roomHeader}>
                        <h3 className={styles.roomTitle}>{room.roomType || 'Apartment'}</h3>
                        <div className={styles.availabilityTag}>
                            <span className={styles.availabilityIcon}>🏠</span>
                            {room.quantityAvailable || 0} available
                        </div>
                    </div>
                    
                    <p className={styles.roomDescription}>{room.description || 'No description provided'}</p>

                    {room.amenities?.length > 0 && (
                        <div className={styles.roomAmenities}>
                            {room.amenities.slice(0, 3).map((amenity, index) => (
                                <span key={index} className={styles.amenityTag}>
                                    {amenity}
                                </span>
                            ))}
                            {room.amenities.length > 3 && (
                                <span className={styles.amenityMore}>
                                    +{room.amenities.length - 3} more
                                </span>
                            )}
                        </div>
                    )}

                    <div className={styles.roomMeta}>
                        <div className={styles.roomInfo}>
                            <span className={styles.infoLabel}>Monthly Rate:</span>
                            <span className={styles.infoValue}>XAF {room.price?.toLocaleString() || '0'}</span>
                        </div>
                        <div className={styles.roomInfo}>
                            <span className={styles.infoLabel}>Yearly Rate:</span>
                            <span className={styles.infoValue}>XAF {(room.price * 12)?.toLocaleString() || '0'}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.roomPrice}>
                    <div className={styles.priceWrapper}>
                        <div className={styles.priceAmount}>
                            XAF {room.price?.toLocaleString() || '0'}
                        </div>
                        <div className={styles.pricePeriod}>per month</div>
                        {/* <button className={styles.bookButton} onClick={() => onSelectRoom(room)}>
                            Select Apartment
                            <span className={styles.bookArrow}>→</span> */}
                        <button 
                            className={styles.bookButton} 
                            onClick={() => onSelectRoom(room)}
                            disabled={isVerifying}
                        >
                            {isVerifying ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    Select Apartment
                                    <span className={styles.bookArrow}>→</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Fullscreen Modal */}
            {isImageFullscreen && room.files?.length > 0 && createPortal(
                <div className={styles.fullscreenModal} onClick={closeFullscreen}>
                    <div className={styles.fullscreenContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeFullscreen} onClick={closeFullscreen}>
                            ✕
                        </button>
                        <img 
                            src={`${room.files[currentImageIndex]?.url}`}
                            alt={`${room.roomType} - Fullscreen view`}
                            className={styles.fullscreenImage}
                        />
                        {room.files.length > 1 && (
                            <>
                                <button 
                                    className={`${styles.fullscreenArrow} ${styles.fullscreenArrowLeft}`}
                                    onClick={prevImage}
                                >
                                    ‹
                                </button>
                                <button 
                                    className={`${styles.fullscreenArrow} ${styles.fullscreenArrowRight}`}
                                    onClick={nextImage}
                                >
                                    ›
                                </button>
                                <div className={styles.fullscreenIndicators}>
                                    {room.files.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`${styles.fullscreenIndicator} ${index === currentImageIndex ? styles.active : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentImageIndex(index);
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default RentRoomList;